import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Navbar from "./components/Navbar";
import ScrollToTop from "./components/ScrollToTop"; // Import Scroll-to-Top button
import Footer from "./components/Footer";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import Loading from "./pages/Loading";

// Lazy loading pages for better performance
const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const Projects = lazy(() => import("./pages/Projects"));
const Blog = lazy(() => import("./pages/Blog"));
const Clients = lazy(() => import("./pages/Clients"));
const Springboard = lazy(() => import("./pages/Springboard"));
const Contact = lazy(() => import("./pages/Contact"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const ExploreOurTeam = lazy(() => import("./pages/ExploreOurTeam"));

// --------------------- PROJECT PAGES ---------------------
const Agewiser = lazy(() => import("./pages/CaseStudiesPages/Agewiser"));
const CareerVI = lazy(() => import("./pages/CaseStudiesPages/CareerVI"));
const Chronos = lazy(() => import("./pages/CaseStudiesPages/Chronos"));
const Doclinq = lazy(() => import("./pages/CaseStudiesPages/Doclinq"));
const Interoads = lazy(() => import("./pages/CaseStudiesPages/Interoads"));
const NinjaHR = lazy(() => import("./pages/CaseStudiesPages/NinjaHR"));
const Proctor8 = lazy(() => import("./pages/CaseStudiesPages/Proctor8"));
const SafrCare = lazy(() => import("./pages/CaseStudiesPages/SafrCare"));

// =================== SERVICES PAGES ===================
const AIDevelopment = lazy(() =>
  import("./pages/AllServicesPages/AIDevelopment")
);
const CustomSoftwereDev = lazy(() =>
  import("./pages/AllServicesPages/CustomSoftwereDev")
);
const DigitalMarketing = lazy(() =>
  import("./pages/AllServicesPages/DigitalMarketing")
);
const DevOops = lazy(() => import("./pages/AllServicesPages/DevOops"));
const CustomAppDevelopment = lazy(() =>
  import("./pages/AllServicesPages/CustomAppDevelopment")
);
const GPTIntegeration = lazy(() =>
  import("./pages/AllServicesPages/GPTIntegeration")
);
const ComputerVision = lazy(() =>
  import("./pages/AllServicesPages/ComputerVision")
);
const NaturalLanguage = lazy(() =>
  import("./pages/AllServicesPages/NaturalLanguage")
);
const PredictiveModeling = lazy(() =>
  import("./pages/AllServicesPages/PredictiveModeling")
);
const MachineLearning = lazy(() =>
  import("./pages/AllServicesPages/MachineLearning")
);
const StaffAugmentation = lazy(() =>
  import("./pages/AllServicesPages/StaffAugmentation")
);
const AIChatbootDev = lazy(() =>
  import("./pages/AllServicesPages/AIChatbootDev")
);
const WebDevelopment = lazy(() =>
  import("./pages/AllServicesPages/WebDevelopment")
);
const UIUXDesign = lazy(() => import("./pages/AllServicesPages/UIUXDesign"));

// Animation Wrapper
const AnimatedComponent = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, x: -100 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: 100 }}
    transition={{ duration: 0.3 }}
  >
    {children}
  </motion.div>
);

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<Loading />}>
        <Routes location={location} key={location.pathname}>
          <Route
            path="/"
            element={
              <AnimatedComponent>
                <Home />
              </AnimatedComponent>
            }
          />
          <Route
            path="/about"
            element={
              <AnimatedComponent>
                <About />
              </AnimatedComponent>
            }
          />
          <Route
            path="/services"
            element={
              <AnimatedComponent>
                <Services />
              </AnimatedComponent>
            }
          />
          <Route
            path="/projects"
            element={
              <AnimatedComponent>
                <Projects />
              </AnimatedComponent>
            }
          />
          <Route
            path="/explore"
            element={
              <AnimatedComponent>
                <ExploreOurTeam />
              </AnimatedComponent>
            }
          />

          {/* Project Sub Pages */}
          <Route
            path="/projects/agewiser"
            element={
              <AnimatedComponent>
                <Agewiser />
              </AnimatedComponent>
            }
          />
          <Route
            path="/projects/career-vi"
            element={
              <AnimatedComponent>
                <CareerVI />
              </AnimatedComponent>
            }
          />
          <Route
            path="/projects/chronos"
            element={
              <AnimatedComponent>
                <Chronos />
              </AnimatedComponent>
            }
          />
          <Route
            path="/projects/doclinq"
            element={
              <AnimatedComponent>
                <Doclinq />
              </AnimatedComponent>
            }
          />
          <Route
            path="/projects/interoads"
            element={
              <AnimatedComponent>
                <Interoads />
              </AnimatedComponent>
            }
          />
          <Route
            path="/projects/ninja-hr"
            element={
              <AnimatedComponent>
                <NinjaHR />
              </AnimatedComponent>
            }
          />
          <Route
            path="/projects/proctor8"
            element={
              <AnimatedComponent>
                <Proctor8 />
              </AnimatedComponent>
            }
          />
          <Route
            path="/projects/safrCare"
            element={
              <AnimatedComponent>
                <SafrCare />
              </AnimatedComponent>
            }
          />

          <Route
            path="/blog"
            element={
              <AnimatedComponent>
                <Blog />
              </AnimatedComponent>
            }
          />
          <Route
            path="/clients"
            element={
              <AnimatedComponent>
                <Clients />
              </AnimatedComponent>
            }
          />
          <Route
            path="/springboard"
            element={
              <AnimatedComponent>
                <Springboard />
              </AnimatedComponent>
            }
          />
          <Route
            path="/contact"
            element={
              <AnimatedComponent>
                <Contact />
              </AnimatedComponent>
            }
          />
          <Route
            path="/terms"
            element={
              <AnimatedComponent>
                <Terms />
              </AnimatedComponent>
            }
          />
          <Route
            path="/privacy"
            element={
              <AnimatedComponent>
                <Privacy />
              </AnimatedComponent>
            }
          />

          {/* Services Sub Pages */}
          <Route
            path="/services/ui-ux-design"
            element={
              <AnimatedComponent>
                <UIUXDesign />
              </AnimatedComponent>
            }
          />
          <Route
            path="/services/mobile-app-development"
            element={
              <AnimatedComponent>
                <CustomAppDevelopment />
              </AnimatedComponent>
            }
          />
          <Route
            path="/services/chat-gpt-integrations"
            element={
              <AnimatedComponent>
                <GPTIntegeration />
              </AnimatedComponent>
            }
          />
          <Route
            path="/services/computer-vision"
            element={
              <AnimatedComponent>
                <ComputerVision />
              </AnimatedComponent>
            }
          />
          <Route
            path="/services/natural-language-processing"
            element={
              <AnimatedComponent>
                <NaturalLanguage />
              </AnimatedComponent>
            }
          />
          <Route
            path="/services/ai-development"
            element={
              <AnimatedComponent>
                <AIDevelopment />
              </AnimatedComponent>
            }
          />
          <Route
            path="/services/predictive-modeling"
            element={
              <AnimatedComponent>
                <PredictiveModeling />
              </AnimatedComponent>
            }
          />
          <Route
            path="/services/machine-learning"
            element={
              <AnimatedComponent>
                <MachineLearning />
              </AnimatedComponent>
            }
          />
          <Route
            path="/services/staff-augmentation"
            element={
              <AnimatedComponent>
                <StaffAugmentation />
              </AnimatedComponent>
            }
          />
          <Route
            path="/services/ai-chat-development"
            element={
              <AnimatedComponent>
                <AIChatbootDev />
              </AnimatedComponent>
            }
          />
          <Route
            path="/services/custom-software"
            element={
              <AnimatedComponent>
                <CustomSoftwereDev />
              </AnimatedComponent>
            }
          />
          <Route
            path="/services/digital-marketing"
            element={
              <AnimatedComponent>
                <DigitalMarketing />
              </AnimatedComponent>
            }
          />
          <Route
            path="/services/devops"
            element={
              <AnimatedComponent>
                <DevOops />
              </AnimatedComponent>
            }
          />
          <Route
            path="/services/custom-web-development"
            element={
              <AnimatedComponent>
                <WebDevelopment />
              </AnimatedComponent>
            }
          />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

const App = () => {
  return (
    <Router>
      <Navbar />
      <div
        style={{ borderRadius: "15px", overflow: "hidden", marginTop: "110px" }}
      >
        <AnimatedRoutes />
      </div>
      <Footer />
      <ScrollToTop />
    </Router>
  );
};

export default App;
